import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Toggles an element in and out of fullscreen.
 * @callback toggleFullscreen
 */

/**
 * @typedef FullscreenFallbackProps
 * @type {object}
 * @property {object} ref Ref to apply to component.
 * @property {boolean} fullscreenEnabled Whether device supports fullscreen. When true, fallback should do nothing.
 * @property {(newState: boolean) => void} onChange Callback invoked when fallback state changes between active and inactive.
 */

/**
 * Custom hook for making an element fullscreen.
 * @param {object} elementRef Ref of element.
 * @returns {[toggleFullscreen, boolean, boolean, FullscreenFallbackProps]} Returns function to toggle element in and out of fullscreen, and a boolean indicating whether it is currently fullscreen.
 */
const useFullscreen = (elementRef) => {
    // Whether element is in fullscreen mode
    const [isFullscreen, setIsFullscreen] = useState(false);

    const fullscreenFallbackRef = useRef();

    // Function to enter/exit fullscreen mode
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenEnabled && fullscreenFallbackRef.current) {
            // If using fallback, invoke function attached to its refs
            fullscreenFallbackRef.current.toggleFullscreen();
        } else if (document.fullscreenElement) {
            // Exit fullscreen
            document.exitFullscreen().catch(console.error);
        } else {
            // Enter fullscreen
            const element = elementRef.current;
            if (element) {
                const requestFullscreen = element.requestFullscreen ?? element.webkitRequestFullscreen;
                if (requestFullscreen) {
                    requestFullscreen.call(element)
                        .catch(console.error);
                } else {
                    console.error('Cannot request fullscreen');
                }
            }
        }
    }, [elementRef]);

    // Set up listener to update state when we enter and exit fullscreen mode
    useEffect(() => {
        if (document.fullscreenEnabled) {
            const onChange = () => {
                setIsFullscreen(!!document.fullscreenElement);
            }

            const element = elementRef.current;

            element.addEventListener('fullscreenchange', onChange);

            return () => element.removeEventListener('fullscreenchange', onChange);
        }
    }, [elementRef]);

    return [
        toggleFullscreen,
        isFullscreen,
        // Fullscreen is available either if browser supports it or if the fallback component is being rendered
        document.fullscreenEnabled || !!fullscreenFallbackRef.current,
        {
            ref: fullscreenFallbackRef,
            fullscreenEnabled: document.fullscreenEnabled,
            // When using fallback, state is updated through a prop callback
            onChange: setIsFullscreen
        }
    ];
}

export default useFullscreen;